/**
 * Hook para consumir la API del Mentor.
 *
 * Uso:
 *   const { data, loading, error } = useBotAPI<AnalysisData>('/api/analisis');
 *   const { data, refetch } = useBotAPI<SetupData>('/api/setups');
 */

import { useState, useEffect, useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.asistentetradingia.com';
const API_KEY = import.meta.env.VITE_API_KEY || '';

export interface BotAnalysis {
  timestamp: string;
  timeframe: string;
  daily_bias: string;
  bias_confidence: number;
  current_zone: string;
  confluence_count: number;
  kill_zone_active: string | null;
  setup: BotSetup | null;
  fair_value_gaps: any[];
  order_blocks: any[];
  liquidity_sweeps: any[];
  market_structures: any[];
  _generated: string;
}

export interface BotSetup {
  direction: string;
  entry_zone_low: number;
  entry_zone_high: number;
  stop_loss: number;
  take_profit: number;
  risk_points: number;
  reward_points: number;
  rr_ratio: number;
  confluences: string[];
  wait_for: string;
  reason: string;
}

export interface BacktestStats {
  total_trades: number;
  win_rate: number;
  profit_factor: number;
  total_r: number;
  avg_r_per_trade: number;
  validated_config: {
    direction: string;
    min_confluences: number;
    sessions: string[];
    excluded: string[];
    min_rr: number;
  };
  edges: {
    name: string;
    status: string;
    oos_windows?: string;
    trades?: number;
    win_rate?: number;
    total_r?: number;
    note?: string;
    reason?: string;
  }[];
  patterns_found?: number;
}

export interface RiskStatus {
  current_balance: number;
  profit: number;
  mll_level: number;
  distance_to_mll: number;
  risk_level: 'GREEN' | 'YELLOW' | 'RED';
  max_minis: number;
  max_micros: number;
  account: {
    type: string;
    initial_balance: number;
    mll_type: string;
    max_drawdown: number;
  };
}

export interface TradesResponse {
  trades: any[];
  stats: {
    total: number;
    closed: number;
    open: number;
    winners: number;
    losers: number;
    win_rate: number;
    total_pnl: number;
  };
}

export interface MentorTextResponse {
  analysis_text: string;
  timestamp: string;
}

export interface NewsResponse {
  today: any[];
  upcoming: any[];
}

export interface SetupTracking {
  id: string;
  direction: string;
  entry_min: number;
  entry_max: number;
  sl: number;
  tp: number;
  rr_ratio: number;
  status: string;
  outcome: string | null;
  pnl_usd: number | null;
  setup_type: string;
  session: string;
  close_reason: string | null;
  triggered: boolean;
}

export interface SetupTrackingResponse {
  date: string;
  total_setups: number;
  won: number;
  lost: number;
  pending: number;
  expired: number;
  timeout: number;
  total_usd: number;
  setups: SetupTracking[];
}

interface UseBotAPIResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBotAPI<T>(endpoint: string): UseBotAPIResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        headers: { 'X-API-Key': API_KEY },
      });
      if (!res.ok) {
        throw new Error(`API error: ${res.status} ${res.statusText}`);
      }
      const json = await res.json();
      setData(json);
    } catch (err: any) {
      setError(err.message || 'Error conectando con el mentor');
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60_000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export default useBotAPI;
